import { AuthProvider as RAAuthProvider } from 'react-admin';
import { login } from '../pages/paths';
import { getRolesFromTokenData, getTokenData, tokenManager } from '../utils/token';

interface AuthProviderLocalState {
    user: {
        data: any;
        refreshToken: string | null;
        loggedIn: boolean;
    };
    loaded: boolean;
}

export const localState: AuthProviderLocalState = {
    user: {
        data: null,
        refreshToken: null,
        loggedIn: false,
    },
    loaded: false,
};

/*export const loadLocalStorageInLocalState = () => {
    localState.user.refreshToken = localStorage.getItem(
        // @ts-ignore
        process.env.REACT_APP_LOCALSTORAGE_REFRESH_TOKEN_VARIABLE
    );
    localState.loaded = true;
};*/

export type AuthProvider = RAAuthProvider & {
    refreshToken: (params: { refreshToken: string }) => Promise<{ token: string; refreshToken: string } | undefined>;
};

const entrypoint = process.env.REACT_APP_API_ENTRYPOINT || '';
const loginEndpoint = `${entrypoint}/json_check`;
const refreshEndpoint = `${entrypoint}/token/refresh`;

const exchangeRefreshToken = async (
    refreshToken: string,
): Promise<{ token: string; refreshToken: string } | undefined> => {
    const response = await fetch(refreshEndpoint, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
    });

    let data: { token?: string; refresh_token?: string } | null = null;

    try {
        data = await response.json();
    } catch {
        data = null;
    }

    if (!data?.token || !data?.refresh_token) {
        return undefined;
    }

    tokenManager.setTokens(data.token);
    tokenManager.setRefreshToken(data.refresh_token);

    return {
        token: data.token,
        refreshToken: data.refresh_token,
    };
};

const authProvider: AuthProvider = {
    login: async (params: { username?: string; password?: string }): Promise<{ token: string; refreshToken?: string }> => {
        const username = params?.username?.trim();
        const password = params?.password;

        if (!username || !password) {
            return Promise.reject(new Error('Missing credentials'));
        }

        const response = await fetch(loginEndpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/ld+json',
            },
            body: JSON.stringify({ username, password }),
        });

        let data: { token?: string; refresh_token?: string; refreshToken?: string } | null = null;

        try {
            data = await response.json();
        } catch {
            data = null;
        }

        if (!response.ok || !data?.token) {
            return Promise.reject(new Error('Invalid credentials'));
        }

        const refreshToken = data.refresh_token || data.refreshToken;

        tokenManager.setTokens(data.token);

        if (refreshToken) {
            tokenManager.setRefreshToken(refreshToken);
            localState.user.refreshToken = refreshToken;
        }

        localState.user.loggedIn = true;
        localState.loaded = true;

        // /json_check may return a token without the full role set; exchange the
        // refresh token so login matches the /c/:refreshToken entry flow.
        let accessToken = data.token;
        let activeRefreshToken = refreshToken;

        if (refreshToken) {
            const refreshed = await exchangeRefreshToken(refreshToken);
            if (refreshed) {
                accessToken = refreshed.token;
                activeRefreshToken = refreshed.refreshToken;
                localState.user.refreshToken = refreshed.refreshToken;
            }
        }

        return {
            token: accessToken,
            refreshToken: activeRefreshToken,
        };
    },
    refreshToken: (params: { refreshToken: string }): Promise<{ token: string; refreshToken: string } | undefined> => {
        return exchangeRefreshToken(params.refreshToken).then((tokens) => {
            if (tokens) {
                localState.user.loggedIn = true;
                localState.loaded = true;
                localState.user.refreshToken = tokens.refreshToken;
            }

            return tokens;
        });
    },
    logout: (params: any): Promise<void | string> => {
        tokenManager.clearAll();
        localState.user.loggedIn = false;
        localState.user.refreshToken = null;
        return Promise.resolve();
    },

    checkAuth: async (params: any): Promise<void> => {
        // @ts-ignore
        // const tokenVariable = localStorage.getItem(process.env.REACT_APP_LOCALSTORAGE_TOKEN_VARIABLE);
        // if (tokenVariable) {
        //     const decodedToken = decodeJwt(tokenVariable);
        //     console.log("tokenVariable", tokenVariable);
        // }

        // @ts-ignore
        // return localStorage.getItem(process.env.REACT_APP_LOCALSTORAGE_TOKEN_VARIABLE) ? Promise.resolve() : Promise.reject();
        return Promise.resolve();
    },
    checkError: (error: any): Promise<void> => {
        const status =
            'status' in error
                ? error.status
                : 'response' in error && 'status' in error.response
                ? error.response.status
                : null;
        if (status) {
            if (status === 401 || status === 403) {
                localState.user.loggedIn = false;

                return Promise.reject({ redirectTo: login });
            }
        }

        return Promise.resolve();
    },
    getPermissions: async (params: any): Promise<any> => {
        if (localState.loaded && !localState.user.loggedIn) {
            return [];
        }

        const token = tokenManager.getToken();
        const tokenData = token ? getTokenData(token) : null;

        return getRolesFromTokenData(tokenData);
    },
    // getIdentity: () => {
    //     return getUserConnected().then((user: DUser) => user);
    // }
};

export default authProvider;
